import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, Observable, of, take, withLatestFrom } from 'rxjs';
import { ModalService } from '@shared/components/modal-list/modal-list.service';
import { AppLoad } from '@core/contracts/app-load';
import { SelectB2bUnitModalComponent } from '@b2b/features/b2b-unit/components/select-b2b-unit-modal/select-b2b-unit-modal.component';
import { B2BUnit, GetAvailableB2BUnitsResponse, PaymentMethod } from '@b2b/features/b2b-unit/core/b2b-unit.model';
import { getB2BUnit } from '@b2b/features/b2b-unit/core/store/b2b-unit.actions';
import {
  selectCurrentB2bUnit as selectCurrentB2BUnit,
  selectDefaultPaymentMethod,
  selectPaymentMethods,
} from '@b2b/features/b2b-unit/core/store/b2b-unit.selectors';
import { CartService } from '@b2b/features/cart/core/cart.service';
import { UserService } from '@app/core/user/user.service';
import { GenericRequest, GenericState } from '@app/core/models/request-status.model';
import { B2bUnitConnector } from '@b2b/features/b2b-unit/core/b2b-unit.connector';
import { AUTH, B2B_UNIT_ID_KEY } from '@shared/tools/constants';
import { getOrCreateCart } from '@b2b/features/cart/core/store/cart.actions';
import { AppFeaturesService } from '@core/services/app-features.service';
import { getCommerceStorageContext, getCommerceStorageKeys } from '@shared/tools/storage-keys';

@Injectable({
  providedIn: 'root',
})
export class B2BUnitService implements AppLoad {
  constructor(
    private store: Store,
    private modalService: ModalService,
    private userService: UserService,
    private cartService: CartService,
    private connector: B2bUnitConnector,
    private appFeaturesService: AppFeaturesService,
  ) {}

  load(): Observable<void> {
    if (!this.appFeaturesService.isB2BEnabled() && !this.appFeaturesService.isASMEnabled()) {
      return of(void 0);
    }

    const storageContext = getCommerceStorageContext();
    const b2bUnitId = localStorage.getItem(getCommerceStorageKeys(storageContext).unitId);

    if (b2bUnitId) {
      this.addB2BUnitToStore(b2bUnitId);
    } else {
      const isAuthenticated = !!localStorage.getItem(AUTH);
      if (isAuthenticated && storageContext === 'b2b') {
        this.loadDefaultUnitOrShowModal();
      }
    }

    return of(void 0);
  }

  /**
   * Verifica se existe uma B2BUnit default para o customer.
   * Se existir, seleciona automaticamente. Caso contrário, abre a modal de seleção.
   */
  loadDefaultUnitOrShowModal(): void {
    this.connector
      .getDefaultB2BUnit()
      .pipe(take(1))
      .subscribe({
        next: defaultUnit => {
          if (defaultUnit && defaultUnit.uid) {
            localStorage.setItem(B2B_UNIT_ID_KEY, defaultUnit.uid);
            this.addB2BUnitToStore(defaultUnit.uid);
            this.store.dispatch(getOrCreateCart());
          } else {
            this.showSelectionModal();
          }
        },
        error: () => {
          this.showSelectionModal();
        },
      });
  }

  addB2BUnitToStore(b2bUnitId: string): void {
    this.store.dispatch(getB2BUnit({ b2bUnitId }));
  }

  getCurrentB2BUnit(): Observable<GenericState<B2BUnit | null>> {
    return this.store.select(selectCurrentB2BUnit);
  }

  searchB2Bunits(searchTerms: { [key: string]: string | number } = {}): Observable<GetAvailableB2BUnitsResponse> {
    return this.connector.getAvailableB2bUnits(searchTerms);
  }

  showSelectionModal(preselectedB2bUnitId?: string): void {
    const isAuthenticated = !!localStorage.getItem(AUTH);
    if (!isAuthenticated) return;

    this.userService
      .get()
      .pipe(take(1))
      .subscribe(() => {
        this.modalService.addModal({
          id: 'select-b2b-unit-modal',
          classList: 'select-b2b-unit-modal',
          disableClose: !localStorage.getItem(B2B_UNIT_ID_KEY),
          hasCloseButton: !!localStorage.getItem(B2B_UNIT_ID_KEY),
          component: SelectB2bUnitModalComponent,
          data: { preselectedB2bUnitId },
        });
      });
  }

  closeSelectionModal(): void {
    this.modalService.close(['select-b2b-unit-modal']);
  }

  getPaymentMethods(): Observable<GenericRequest<PaymentMethod[]>> {
    return this.store.select(selectPaymentMethods);
  }

  getDefaultPaymentMethod(): Observable<PaymentMethod | null> {
    return this.store.select(selectDefaultPaymentMethod).pipe(map(state => state.data || null));
  }

  decidePaymentMethodId(): Observable<string> {
    return this.getPaymentMethods().pipe(
      withLatestFrom(this.getDefaultPaymentMethod(), this.cartService.getCart()),
      map(([items, defaultPaymentMethod, cart]) => {
        if (cart.data?.paymentMethod && items.data?.some(i => i.id === cart.data?.paymentMethod)) {
          return cart.data?.paymentMethod;
        } else if (defaultPaymentMethod && items.data?.some(i => i.id === defaultPaymentMethod.id)) {
          return defaultPaymentMethod.id;
        }
        return '';
      }),
    );
  }
}
